import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { filterVisibleCategories } from "@/hooks/useCategories";

/**
 * DeoDap-style category discovery grid for home page.
 * Mobile: 3 cols · sm: 4 · md: 5 · lg: 6.
 * 1:1 image area filling ~78% of card height, name centered below.
 * Tapping a card deep-links to /marketplace?cat=<name>.
 *
 * Robust data strategy (defense-in-depth against the "1 card stuck" regression):
 *   1. Fetch BOTH featured + all in parallel.
 *   2. Use admin-curated featured ONLY if there are ≥4 such cards (curated rail).
 *   3. Otherwise show all enabled categories so rail never collapses.
 *   4. SAFETY NET: if the picked list ends up with < 3 cards but allEnabled has more,
 *      fall back to allEnabled regardless (prevents accidental misconfiguration).
 *   5. If both API calls fail / return empty, log a diagnostic and the section
 *      hides gracefully (no skeleton stuck, no empty grid).
 */
export default function ShopByCategories({ limit = 12 }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [featured, all] = await Promise.all([
          api.get("/categories?featured=true").catch(() => ({ data: [] })),
          api.get("/categories").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        // Defense-in-depth: strip "Others" / hidden / count=0 client-side too so
        // a stale service-worker cache can't leak them back into the grid.
        const featuredList = filterVisibleCategories(featured.data || []);
        const allEnabled = filterVisibleCategories(all.data || []);
        // Primary pick: curated featured (≥4) OR all enabled.
        let picked = featuredList.length >= 4 ? featuredList : allEnabled;
        // Safety net: if picked is too small but we have more categories, prefer the larger list.
        if (picked.length < 3 && allEnabled.length > picked.length) {
          picked = allEnabled;
        }
        // Diagnostic: surface when we end up with a sparse rail in production
        if (picked.length === 0 || picked.length === 1) {
          // eslint-disable-next-line no-console
          console.warn("[ShopByCategories] sparse rail:", {
            featured: featuredList.length, all: allEnabled.length, picked: picked.length,
          });
        }
        setList(picked.slice(0, limit));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  if (loading) {
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="animate-pulse rounded-xl bg-[#F5B6C8]/60 border border-[rgba(11,31,58,0.15)] p-1.5 sm:p-2">
            <div className="aspect-square rounded-lg bg-amethyst-100/70" />
            <div className="h-3 w-3/4 bg-amethyst-100/70 rounded mt-1.5 mx-auto" />
          </div>
        ))}
      </div>
    );
  }

  if (!list.length) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="shop-by-categories">
      <div className="max-w-7xl mx-auto">
        {/* Heading — bold with magenta underline accent */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="inline-block text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-amethyst-900 tracking-tight relative pb-2">
            <span className="flex items-center justify-center gap-2">
              <ShoppingBag className="w-6 h-6 text-magenta-600" strokeWidth={1.8} />
              Shop by Categories
            </span>
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-1 rounded-full bg-gradient-to-r from-magenta-500 via-gold-400 to-magenta-500" aria-hidden="true" />
          </h2>
        </div>

        {/* DeoDap-style compact grid: 3 cols mobile, scales up on larger screens */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
          {list.map((c) => (
            <Link
              key={c.id || c.name}
              to={`/marketplace?cat=${encodeURIComponent(c.name)}`}
              className="group block rounded-xl bg-[#F5B6C8] border border-[rgba(11,31,58,0.25)] p-1.5 sm:p-2 shadow-[0_4px_14px_rgba(11,31,58,0.08)] hover:shadow-[0_10px_28px_rgba(11,31,58,0.14)] hover:border-[#0B1F3A]/50 transition-all duration-200 active:scale-[0.98] hover:scale-[1.02] will-change-transform"
              data-testid={`home-cat-${c.slug || c.name}`}
            >
              {/* Image area — 1:1 square, compact rounded */}
              <div className="w-full aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-amethyst-50 to-magenta-50/40">
                {c.image_url ? (
                  <img
                    src={c.image_url}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl sm:text-4xl">
                    {c.icon || "🛍️"}
                  </div>
                )}
              </div>
              {/* Name — 13-14px, semibold, max 2 lines, center */}
              <p className="mt-1 sm:mt-1.5 text-[13px] sm:text-[14px] font-semibold text-amethyst-900 line-clamp-2 leading-tight text-center px-0.5">
                {c.name}
              </p>
            </Link>
          ))}
        </div>

        {/* View All */}
        <div className="mt-6 sm:mt-8 text-center">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-magenta-700 hover:text-magenta-800 transition-colors"
            data-testid="view-all-categories"
          >
            View All Categories
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
